import { useEffect, useState } from "react";
import { MessageSquare, ThumbsUp, ThumbsDown, Reply } from "lucide-react";
import { API, errMsg, fmtDate } from "../lib/api";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";

function CommentBlock({ c, onReact, onStartReply, depth }) {
  const likeActive = c.my_reaction === "like";
  const dislikeActive = c.my_reaction === "dislike";
  return (
    <div className={depth ? "ml-5 pl-3 border-l-2 border-border mt-3" : ""} data-testid={`comment-${c.id}`}>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="font-semibold">{c.author}</span>
        {c.is_admin && (
          <span className="bg-primary text-primary-foreground px-1.5 py-0.5 rounded text-[10px] font-bold tracking-wide">TEAM</span>
        )}
        <span className="text-muted-foreground">{fmtDate(c.created_at)}</span>
      </div>
      <p className="mt-1 text-sm leading-relaxed whitespace-pre-wrap">{c.body}</p>
      <div className="mt-1.5 flex items-center gap-4">
        <button type="button" data-testid={`like-comment-${c.id}`} onClick={() => onReact(c, "like")}
          aria-label="Like comment"
          className={`flex items-center gap-1 text-xs font-medium transition-colors ${likeActive ? "text-success" : "text-muted-foreground hover:text-foreground"}`}>
          <ThumbsUp className="w-3.5 h-3.5" /> {c.likes}
        </button>
        <button type="button" data-testid={`dislike-comment-${c.id}`} onClick={() => onReact(c, "dislike")}
          aria-label="Dislike comment"
          className={`flex items-center gap-1 text-xs font-medium transition-colors ${dislikeActive ? "text-destructive" : "text-muted-foreground hover:text-foreground"}`}>
          <ThumbsDown className="w-3.5 h-3.5" /> {c.dislikes}
        </button>
        {depth === 0 && (
          <button type="button" data-testid={`reply-comment-${c.id}`} onClick={() => onStartReply(c.id)}
            className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors">
            <Reply className="w-3.5 h-3.5" /> Reply
          </button>
        )}
      </div>
    </div>
  );
}

export function QuestionDiscussion({ questionId, suffix = "" }) {
  const [comments, setComments] = useState(null);
  const [body, setBody] = useState("");
  const [posting, setPosting] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [replyBody, setReplyBody] = useState("");
  const [replying, setReplying] = useState(false);

  const load = () => {
    API.get(`/questions/${questionId}/comments`)
      .then(({ data }) => setComments(data))
      .catch(() => setComments([]));
  };

  useEffect(() => {
    setComments(null);
    setReplyTo(null);
    setReplyBody("");
    load();
  }, [questionId]);

  const post = async () => {
    if (!body.trim()) return;
    setPosting(true);
    try {
      await API.post(`/questions/${questionId}/comments`, { body });
      setBody("");
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setPosting(false); }
  };

  const postReply = async (parentId) => {
    if (!replyBody.trim()) return;
    setReplying(true);
    try {
      await API.post(`/questions/${questionId}/comments`, { body: replyBody, parent_id: parentId });
      setReplyBody("");
      setReplyTo(null);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setReplying(false); }
  };

  const react = async (c, r) => {
    const next = c.my_reaction === r ? "none" : r;
    try {
      await API.post(`/comments/${c.id}/react`, { reaction: next });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <section className="bg-card border border-border rounded-lg p-5" data-testid={`discussion-section${suffix}`}>
      <h3 className="flex items-center gap-2 text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground mb-1">
        <MessageSquare className="w-4 h-4" /> Discussion {comments ? `(${comments.length})` : ""}
      </h3>
      <p className="text-[11px] text-muted-foreground mb-4">Comments may discuss answers — attempt the question first.</p>

      <Textarea data-testid={`comment-input${suffix}`} rows={3} value={body} onChange={(e) => setBody(e.target.value)}
        placeholder="Share your thoughts on this question…" className="text-sm" />
      <Button size="sm" data-testid={`comment-submit-btn${suffix}`} onClick={post} disabled={posting || !body.trim()} className="mt-2.5">
        {posting ? "Posting…" : "Post comment"}
      </Button>

      <div className="mt-5 space-y-5">
        {comments === null ? (
          <p className="text-xs text-muted-foreground">Loading discussion…</p>
        ) : comments.length === 0 ? (
          <p className="text-xs text-muted-foreground" data-testid="discussion-empty">No comments yet — be the first to share your thoughts.</p>
        ) : (
          comments.map((c) => (
            <div key={c.id}>
              <CommentBlock c={c} onReact={react} onStartReply={(id) => { setReplyTo(id); setReplyBody(""); }} depth={0} />
              {c.replies.map((r) => (
                <CommentBlock key={r.id} c={r} onReact={react} onStartReply={() => {}} depth={1} />
              ))}
              {replyTo === c.id && (
                <div className="ml-5 mt-3" data-testid={`reply-box-${c.id}`}>
                  <Textarea rows={2} value={replyBody} onChange={(e) => setReplyBody(e.target.value)}
                    placeholder="Write a reply…" className="text-sm" data-testid={`reply-input-${c.id}`} />
                  <div className="flex gap-2 mt-2">
                    <Button size="sm" data-testid={`reply-submit-${c.id}`} onClick={() => postReply(c.id)} disabled={replying || !replyBody.trim()}>
                      {replying ? "Posting…" : "Post reply"}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setReplyTo(null)}>Cancel</Button>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </section>
  );
}
